<script context="module">
    export const name = "small view";
</script>

<script lang="ts">
    import Data from "@reperio-news/core.components/blocks/ContentItem/Data";
    import { stripTags } from "@reperio-news/core.components/includes/Helpers";
    import { z } from "zod";
    import { getContext } from "svelte";
    type Data = z.infer<typeof Data>;
    import ImageView from "@reperio-news/core.components/ImageView.svelte";
    import { slugToColor } from "../TopMenu/View.svelte";
    import { getURLRel } from "../../includes/helpers.svelte";
    import TitleTag from "./TitleTag.svelte";

    const context: SvelteStore<any> = getContext("context");
    const c = getContext("parentBlockColumnCount");

    let categoryColor = "red";

    $: if (data && data.content) {
        categoryColor = slugToColor(
            data.content.category?.slug,
            $context.global_blocks?.TopMenu_main_menu?.data?.items
        );
    }

    export let data: Data;
</script>

{#if data && data.content}
    <div class="item co-{$c}">
        <div class="main">
            {#if data.content.poster}
                <a
                    class="image"
                    href={data.content.url}
                    rel={getURLRel(data.content.url)}
                >
                    <ImageView
                        src={data.content.poster}
                        crop={data.content.poster.crop}
                        width={350}
                        height={225}
                        fit="cover"
                    />
                </a>
            {/if}
            <div class="text">
                {#if data.content.category}
                    <a
                        style="color: {categoryColor}"
                        href="//{data.content.domain}/{data.content.category.slug}"
                        class="category">{data.content.category.name}</a
                    >
                {/if}
                <a
                    class="title"
                    href={data.content.url}
                    title={stripTags(data.content.summary)}
                    rel={getURLRel(data.content.url)}><TitleTag content={data.content} />{@html stripTags(data.content.title, ["i", "b", "strong", "font"])}</a
                >
            </div>
        </div>
    </div>
{/if}

<style lang="scss">
    div.co-1 {
        width: calc(100% - 10px);
        justify-content: space-between;
        align-items: center;
        display: flex;
        flex-direction: column;

        .main {
            .image {
                width: 170px;
                aspect-ratio: 14 / 9;
                overflow: hidden;
                margin-right: 12px;
                border-radius: 4px;
            }
        }

        .text .category {
            margin-bottom: 6px;
        }
    }


    .item:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);

        .main .image {
            :global(img) {
                transform: scale(1.05);
            }
        }

        a.title {
            color: #111;
        }
    }

    .item {
        width: 315px;
        flex-grow: 1;
        box-sizing: border-box;
        margin-bottom: 10px;
        margin-right: 5px;

        &.co-2 {
            width: calc(50% - 7px);
        }

        &.co-3,
        &.co-4 {
            width: calc(50% - 7px);
        }
        min-width: 280px;
        overflow: hidden;
        padding: 8px;

        color: #000;
        border-radius: 6px;
        border: 1px solid #f0f0f0;
        position: relative;
        background-color: #fff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
        transition: box-shadow 0.25s ease, transform 0.25s ease;

        .main {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            width: 100%;

            .image {
                width: 140px;
                /* box ratio matches the requested image ratio — no second
                   crop from object-fit on top of the server-side one */
                aspect-ratio: 14 / 9;
                overflow: hidden;
                margin-right: 12px;
                border-radius: 4px;
                flex-shrink: 0;

                :global(img) {
                    transition: transform 0.3s ease;
                }
            }

            .text {
                display: flex;
                flex-direction: column;
                justify-content: center;
                flex: 1;
                min-width: 0;

                .category {
                    font-family: "PT Sans Narrow", sans-serif;
                    font-size: 0.65rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                    line-height: 1;
                    margin-bottom: 4px;
                    opacity: 0.9;
                }

                a.title {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    text-overflow: hidden;
                    white-space: normal;
                    text-overflow: ellipsis;
                    font-family: "Source Sans Pro", sans-serif;
                    font-style: normal;
                    letter-spacing: 0.2px;
                    font-weight: 600;
                    font-size: 0.9rem;
                    line-height: 1.35;
                    color: #1a1a1a;
                    transition: color 0.2s ease;
                }
            }
        }
    }

    :global {
        .carousel-news {
            div.item {
                min-width: calc((var(--parent-width) / 4) - 15px);
            }
        }
    }

    @media only screen and (max-width: 1024px) {
        .item {
            height: auto;
            width: 300px;
        }
    }

    @media only screen and (max-width: 1000px) {
        .co-1 {
            width: calc(50% - 20px);
        }

        :global {
            .carousel-news {
                div.item {
                    min-width: calc(
                        (var(--parent-width) / 3) - 15px
                    ) !important;
                }
            }
        }
    }

    @media only screen and (max-width: 750px) {
        :global {
            .carousel-news {
                div.item {
                    min-width: calc(
                        (var(--parent-width) / 2) - 15px
                    ) !important;
                }
            }
        }
    }

    @media only screen and (max-width: 650px) {
        .co-1 {
            width: auto;
            min-width: 350px;
        }

        :global {
            .carousel-news {
                div.item {
                    min-width: calc(var(--parent-width) - 15px) !important;
                }
            }
        }
    }

    @media only screen and (max-width: 980px) {
        .item {
            width: calc(50% - 30px);
            min-width: 0;

            .main {
                display: flex;
                justify-content: center;
                align-items: center;

                .text a.title {
                    height: auto !important;
                    max-height: 105px !important;
                    font-size: 1em !important;
                    -webkit-line-clamp: 4;
                }
            }
        }
    }

    @media only screen and (max-width: 750px) {
        .item {
            width: 100%;
            min-width: 0;

            &.co-2,
            &.co-3,
            &.co-4 {
                width: 100%;
                min-width: 0;
            }

            .text a.title {
                -webkit-line-clamp: 4;
            }
        }
    }
</style>
